/* ---------------------------------------------------------------------------
   Which register the app is working against.

   The bundled seed in seed.ts is now a fallback rather than the source of truth:
   on boot the client fetches the register from D1 and swaps it in. Everything
   downstream — derive.ts, the screens, the smoke tests — reads through
   getRegister() so neither path needs to know which one it got.

   Keeping the seed as a fallback is deliberate. A field device that cannot reach
   the backend still renders a complete, honest register rather than an empty
   table; registerSource() lets the UI say which one is on screen.
   --------------------------------------------------------------------------- */

import type { Bridge, RegisterSource } from './types.ts';
import { active } from '../tenant/registry.ts';
import { seedFor } from '../tenant/seeds.ts';

/**
 * The register on screen, or null before anything has been read.
 *
 * NULL RATHER THAN THE SEED AT MODULE LOAD, which is the one thing tenancy
 * changed here. This used to initialise to Nepal's bundled records, evaluated
 * when the module was imported — before the tenant is resolved, and therefore
 * unconditionally the wrong department's bridges for a DPWH session until the
 * first fetch landed. Reading the seed lazily means the fallback is whichever
 * department is in force at the moment somebody asks.
 */
let current: Bridge[] | null = null;
let source: RegisterSource = 'seed';

/** The tenant `current` was populated for, so a swap cannot serve stale rows. */
let loadedFor: string | null = null;

/**
 * The register in force — every bridge carried, seed or D1.
 *
 * Falls back to the active tenant's bundled seed, and re-falls-back if the
 * tenant has changed since the rows in hand were adopted. That second condition
 * is what stops a support session that switches department mid-flight from
 * showing one register under the other's letterhead: the rows are dropped, the
 * new tenant's seed appears, and the boot fetch replaces it a moment later.
 *
 * NOT guaranteed non-empty any more. A department with no bundled register and
 * no reachable backend has nothing to show, and the screens say so — see the
 * note in src/tenant/seeds.ts on why that is better than showing somebody
 * else's structures.
 */
export function getRegister(): Bridge[] {
  const tenant = active().id;
  if (current === null || loadedFor !== tenant) {
    current = seedFor(tenant);
    loadedFor = tenant;
    source = 'seed';
  }
  return current;
}

export function registerSource(): RegisterSource {
  return source;
}

/**
 * Adopt records fetched from D1.
 *
 * An empty response is ignored rather than adopted: a database that has not been
 * migrated yet would otherwise blank the whole app, and every screen divides by
 * `list.length` somewhere.
 */
export function setRegister(rows: Bridge[]): boolean {
  if (rows.length === 0) return false;
  current = rows;
  loadedFor = active().id;
  source = 'api';
  return true;
}

/** Drop back to the bundled seed. Used by the tests, and by a tenant switch. */
export function resetRegister(): void {
  current = null;
  loadedFor = null;
  source = 'seed';
}

/** Adopt one record just saved through the edit dialog, in place. */
export function updateOne(bridge: Bridge): void {
  current = getRegister().map((b) => (b.id === bridge.id ? bridge : b));
}
